import React from 'react';

import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { mapIntegrationProviderToDTO } from '@/modules/common/mappers/integration.mapper';
import { IntegrationServices } from '@/modules/common/types/integrations';
import { MissingIntegrationNotice } from '@/modules/onboarding/common/components/resolve-duplicates-modal/missing-integration-notice';
import { ResolveOnboardingConflictsModal } from '@/modules/onboarding/common/components/resolve-duplicates-modal/resolve-onboarding-conflicts-modal';
import {
  MergeRequestBuilder,
  NOT_SPECIFIED_OPTION_VALUE,
  OnboardingResolveConflictAttributeRow,
} from '@/modules/onboarding/common/components/resolve-duplicates-modal/types';
import { invalidateOnboardingTeamMemberQueries } from '@/modules/onboarding/common/utils/onboarding-query-invalidation';
import { removeDuplicatesByKey } from '@/modules/onboarding/common/utils/reconciliation-helpers';

import { mergeEmployees } from '../../api/employees';
import { Employee } from '../../types/employees.types';
import { MergeEmployeesRequest } from '../../types/request/merge-employees.request';

const TEAM_I18N_NAMESPACE = 'authenticated.onboarding.team';

interface IProps {
  selectedItems: Employee[];
  onSuccess?: () => void;
  connectedIntegrations: IntegrationServices[];
  disabled?: boolean;
  disabledTooltip?: string;
  superAdminEmail?: string | null;
}

function getAttributeRows(
  t: (key: string) => string,
  superAdminEmail: string | null
): OnboardingResolveConflictAttributeRow<Employee>[] {
  const emailIsLockedBySuperAdmin = superAdminEmail !== null;

  return [
    {
      key: 'name',
      label: t(`${TEAM_I18N_NAMESPACE}:duplicates.fullName`),
      placeholder: t(`${TEAM_I18N_NAMESPACE}:duplicates.selectName`),
      getSelectOptions: items =>
        removeDuplicatesByKey(items, 'name').map(item => ({
          value: item.name,
          label: item.name,
        })),
      getDisplayValue: item => item.name,
      isEmpty: item => !item.name.trim(),
    },
    {
      key: 'email',
      label: t(`${TEAM_I18N_NAMESPACE}:duplicates.email`),
      placeholder: t(`${TEAM_I18N_NAMESPACE}:duplicates.selectEmail`),
      required: false,
      zodSchema: z
        .string()
        .optional()
        .refine(
          val =>
            !val || val === NOT_SPECIFIED_OPTION_VALUE || z.string().email().safeParse(val).success,
          {
            message: t(`${TEAM_I18N_NAMESPACE}:validation.email.invalid`),
          }
        ),
      getSelectOptions: items => {
        const notSpecifiedOption = {
          value: NOT_SPECIFIED_OPTION_VALUE,
          label: t(`${TEAM_I18N_NAMESPACE}:duplicates.notSpecified`),
          customContent: (
            <span className="text-muted-foreground">
              {t(`${TEAM_I18N_NAMESPACE}:duplicates.notSpecified`)}
            </span>
          ),
        };

        if (emailIsLockedBySuperAdmin) {
          return superAdminEmail ? [{ value: superAdminEmail, label: superAdminEmail }] : [];
        }

        const emailOptions = removeDuplicatesByKey(items, 'email')
          .filter(item => item.email)
          .map(item => ({
            value: item.email as string,
            label: item.email as string,
          }));

        const hasAnyEmptyEmail = items.some(item => !item.email);
        return hasAnyEmptyEmail ? [...emailOptions, notSpecifiedOption] : emailOptions;
      },
      getDisplayValue: item => item.email || t(`${TEAM_I18N_NAMESPACE}:duplicates.notSpecified`),
      isEmpty: item => !item.email,
      showMutedWhenEmpty: true,
      resolveDefaultValue: (items, commonValue) =>
        commonValue || items.some(item => item.email) ? commonValue : NOT_SPECIFIED_OPTION_VALUE,
      selectDisabled: emailIsLockedBySuperAdmin,
      selectDisabledTooltip: emailIsLockedBySuperAdmin
        ? t(`${TEAM_I18N_NAMESPACE}:duplicates.superAdminEmailLockedTooltip`)
        : undefined,
      forcedDefaultValue: superAdminEmail ?? undefined,
    },
  ];
}

const mergeRequestBuilder: MergeRequestBuilder<Employee> = {
  build: (formData, selectedItems, integrationsToUse) => {
    const selectedName = formData.name;
    const selectedEmployee = selectedItems.find(emp => emp.name === selectedName);

    const selectedEmail =
      formData.email && formData.email !== NOT_SPECIFIED_OPTION_VALUE ? formData.email : undefined;

    const request: MergeEmployeesRequest = {
      idsToMerge: selectedItems.map(item => item.id),
      resultingUser: {
        ...(selectedEmail ? { email: selectedEmail } : {}),
        name: selectedName,
        importedFromFilename: selectedEmployee?.importedFromFilename,
        integrationsToUse: integrationsToUse.map(i => ({
          integration: mapIntegrationProviderToDTO(i.integration as IntegrationServices),
          sourceUserId: i.sourceId,
        })),
      },
    };

    return request;
  },
};

export function ResolveOnboardingMembersConflictsModal({
  selectedItems,
  onSuccess,
  connectedIntegrations,
  disabled,
  disabledTooltip,
  superAdminEmail,
}: IProps) {
  const { t } = useTranslation();

  const attributeRows = getAttributeRows(t, superAdminEmail ?? null);

  return (
    <ResolveOnboardingConflictsModal
      selectedItems={selectedItems}
      onSuccess={onSuccess ?? (() => {})}
      attributeRows={attributeRows}
      integrationsList={connectedIntegrations}
      mergeMutationFn={mergeEmployees}
      invalidateQueries={invalidateOnboardingTeamMemberQueries}
      mergeRequestBuilder={mergeRequestBuilder}
      i18nNamespace={TEAM_I18N_NAMESPACE}
      personLabelI18nKey={`${TEAM_I18N_NAMESPACE}:duplicates.person`}
      resultingEntityI18nKey={`${TEAM_I18N_NAMESPACE}:duplicates.resultingEntity`}
      renderFooterNotice={() => <MissingIntegrationNotice i18nNamespace={TEAM_I18N_NAMESPACE} />}
      triggerDisabled={disabled}
      triggerDisabledTooltip={disabledTooltip}
    />
  );
}
